import "./tailwind.css";
import { onIntlError } from '../utils/react/intl-on-error';
import { SearchWrapper } from "./SearchWrapper";
import type { ReactElement, ReactNode } from "react";
import React, { useEffect, useLayoutEffect, useRef, useState, useMemo } from "react";
import { preload } from 'react-dom';
import { IntlProvider } from 'react-intl';
import { defaultLocale } from '../i18n/supported-locales';
import { Messages } from '../types';
import { loadLocaleData } from '../utils/routing/url-helpers';
import { resolvePageLocale } from '../utils/routing/resolve-page-locale';
import { syncHtmlLangAttribute } from '../utils/routing/sync-html-lang-attribute';
import { buildFooterStaticStrings } from '../utils/footer/buildFooterStaticStrings';
import { FooterStringsProvider } from '../contexts/FooterStringsContext';
import { usePageContext } from 'vike-react/usePageContext';
import { useData } from 'vike-react/useData';
import type { PageContextClient } from 'vike/types';
import { SearchProvider } from '../contexts/SearchContext';
import { getIndexName } from '../constants';
import { useGaUserProperties } from '../hooks/useGaUserProperties';
import { useProfilePreferencesSettingsSync } from '../hooks/useProfilePreferencesSettingsSync';
import { ErrorBoundary } from '../design-system/ErrorBoundary';
import { LayoutPageErrorFallback } from '../components/LayoutPageErrorFallback';
import { NavigationProvider, useNavigationOptional } from '../contexts/NavigationContext';
import { ToastProvider } from '../contexts/ToastContext';
import { ToastContainer } from '../design-system/Toast';
import { LocaleMismatchPrompt } from '../components/LocaleMismatchPrompt';
import type { SessionUser } from '../types/auth';
import type { SrpViewModel } from '../features/srp/SrpViewModel';
import { resolveSrpViewModel } from '../features/srp/srp-view-model-wire';
import { useSrpIsland } from '../contexts/SrpIslandContext';
import { AuthProvider, useAuthOptional } from '../contexts/AuthContext';
import { ExperimentProvider } from '../contexts/ExperimentContext';
import { UserProvider } from '../query/UserProvider';
import type { UserStateMap } from '../types/user-state';
import { FeedbackModalProvider } from '../contexts/FeedbackModalContext';
import { JobIslandProvider } from '../contexts/JobIslandContext';
import { SalaryDisplayPreferenceProvider } from '../contexts/SalaryDisplayPreferenceContext';
import { CompanyGroupingPreferenceProvider } from '../contexts/CompanyGroupingPreferenceContext';
import { PayModePreferenceProvider } from '../contexts/PayModePreferenceContext';
import { SrpCardDisplayModeProvider } from '../contexts/SrpCardDisplayModeContext';
import { SrpSalaryDotHoverProvider } from '../contexts/SrpSalaryDotHoverContext';
import { isToolsPage } from '../utils/routing/route-utils';
import { isSearchPagePathname } from '../utils/routing/is-search-page-pathname';
import { isLayoutClientRouteSyncedWithWindow } from '../utils/routing/layout-client-route-synced';
import { layoutPageDataLacksSrpPayload } from '../utils/routing/merge-layout-srp-page-data';
import { useIntl } from 'react-intl';
import { MinimalChrome } from './MinimalChrome';
import { isBackofficePathname } from '../utils/backoffice/is-backoffice-pathname';
import { Provider } from 'react-redux';
import { createStore } from '../redux/createStore';
import { getBackofficeShell, getJobDetailPage, getOutpostShell, getSrpApp, getTeamsShell, isJobDetailPathname } from './route-components';
import { isTeamsPathname } from '../utils/teams/is-teams-pathname';
import { isOutpostPathname } from '../utils/outpost/is-outpost-pathname';
import type { Data as JobData } from '../pages/job/@jobId/@seoSlug/+data';
import { getJobListingHttpStatusCode } from '../utils/job-detail/get-job-listing-http-status-code';
import { parseJobInitialScriptFromDocument } from '../utils/job-detail/parse-job-initial-script';
import { dismissJobPreviewShell, rememberJobPageHeaderBottom } from '../utils/view-transitions/job-preview-shell';
import { CookieConsentBanner } from '../components/CookieConsentBanner';
import { FunctionalConsentPrompt } from '../components/consent/FunctionalConsentPrompt';
import { HiredMomentHost } from '../components/plus/HiredMomentHost';
import { PostSignupAlertNudge } from '../components/job-alerts/PostSignupAlertNudge';
import { SetupIntroDialog } from '../components/setup-guide/SetupIntroDialog';
import { FromAssistantStrip } from '../components/connector/FromAssistantStrip';
import { DemoAccountBanner } from '../components/demo/DemoAccountBanner';
import { PublicDemoBanner } from '../components/demo/PublicDemoBanner';
import { JourneyBridge } from '../src/modules/plus-journey';
import { InspectorMount } from '../i18n-review/adapters/InspectorMount';
import { StaticInspectorMount } from '../i18n-review/adapters/StaticInspectorMount';

const JOB_PATH_WITH_LOCALE = /^\/(?:en|fi|sv)\/(job|tyopaikka|jobb)\/([^/]+)\/([^/]+)(?:\/|$)/i;
const JOB_PATH_NO_LOCALE = /^\/(job|tyopaikka|jobb)\/([^/]+)\/([^/]+)(?:\/|$)/i;

function getJobIdsFromPathname(pathname: string): { jobId: string; companyId: string } | null {
  const path = String(pathname || '');
  let match = path.match(JOB_PATH_WITH_LOCALE);
  if (match) {
    const [, , jobId, companyId] = match;
    return jobId && companyId ? { jobId, companyId } : null;
  }
  match = path.match(JOB_PATH_NO_LOCALE);
  if (match) {
    const [, , jobId, companyId] = match;
    return jobId && companyId ? { jobId, companyId } : null;
  }
  return null;
}

/** Renders the job detail shell; uses useData() when available, with client-side fetch fallback for navigation. */
export function JobRouteContent({ footerStrings }: { footerStrings: Record<string, string> }) {
  // Loaded before render: statically on the server, by pages/+onRenderClient.ts in the browser.
  const JobDetailPage = getJobDetailPage();
  const dataFromHook = useData<JobData>();
  const [clientData, setClientData] = useState<JobData | null>(null);
  /** Synchronous read of `#job-initial` on first client frame so hydration matches SSR (see parseJobInitialScriptFromDocument). */
  const [scriptData, setScriptData] = useState<JobData | null>(() =>
    typeof document !== 'undefined' ? parseJobInitialScriptFromDocument(document) : null,
  );
  const pageContext = usePageContext();
  const pathname =
    (pageContext as any)?.urlPathname ??
    (pageContext as any)?.urlLogical ??
    (typeof window !== 'undefined' ? window.location.pathname : '/');
  const jobLocale = (pageContext?.locale as string) || getLocaleFromPathname(pathname) || defaultLocale;

  // Re-sync after pathname changes (client navigation) or if DOM script updates.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const parsedData = parseJobInitialScriptFromDocument(document);
    if (parsedData?.job) {
      setScriptData(parsedData);
    }
  }, [pathname]);

  const baseData = dataFromHook ?? scriptData;

  // Client-side fallback: when navigating to job URL, useData() may not have run yet; fetch job then.
  // Relative URL: in dev Vite proxies /api/getjob to www.jobcrawls.com (avoids CORS); in prod same-origin.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hasHydratedSimilarJobs =
      !!baseData?.job &&
      Array.isArray(baseData?.similarJobs) &&
      baseData.similarJobs.length > 0;
    if (hasHydratedSimilarJobs) return;
    const ids = getJobIdsFromPathname(pathname);
    if (!ids) return;
    const { jobId, companyId } = ids;
    let cancelled = false;

    const resolveJobBody = async (): Promise<{ job: Record<string, unknown> } | null> => {
      if (baseData?.job) {
        return { job: baseData.job as unknown as Record<string, unknown> };
      }
      const url = `/api/getjob?pk=${encodeURIComponent(jobId)}&sk=${encodeURIComponent(companyId)}`;
      const res = await fetch(url, { headers: { 'user-agent': 'jobcrawls-internal/get-job' } });
      if (!res.ok) return null;
      return (await res.json()) as { job: Record<string, unknown> } | null;
    };

    resolveJobBody()
      .then(async (body) => {
        if (cancelled || !body?.job) return;
        const job = body.job;
        const slugify = (s: string) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
        const seoSlug = job.seoSlug || slugify(`${job.title || ''}-${job.companyId || job.company || ''}-${Array.isArray(job.location) ? job.location[0] : job.location || ''}`);
        const jl = (['en', 'fi', 'sv'].includes(jobLocale) ? jobLocale : 'en') as 'en' | 'fi' | 'sv';
        const primaryLocation = Array.isArray(job.location) ? job.location[0] : job.location || '';
        let similarJobs: JobData['similarJobs'] = [];
        try {
          // Imported here, not at the top: it pulls in the salary aggregation code, and this layout
          // is in every page's bundle while only the job route ever runs this effect.
          const { fetchSimilarJobsFromAlgolia, isFinlandRelevantJob } = await import('../utils/job-detail/similar-jobs-algolia');
          const { similarJobs: sj } = await fetchSimilarJobsFromAlgolia({
            jobObjectId: String((job as { objectID?: string }).objectID ?? jobId),
            normalisedTitle: (job as { normalisedTitle?: string }).normalisedTitle,
            primaryLocation: primaryLocation || '',
            jobLocale: jl,
            indexName: getIndexName(jl),
            // Client-only effect: link to the origin being browsed (dev/staging too), not always prod.
            baseUrl: window.location.origin,
            finlandOnly: isFinlandRelevantJob(job),
          });
          similarJobs = sj;
        } catch {
          /* Algolia optional; cards stay empty */
        }
        if (cancelled) return;
        setClientData({
          job,
          seoSlug,
          metaTitle: '',
          metaDescription: '',
          httpStatusCode: getJobListingHttpStatusCode(job, Date.now()),
          heroImage: undefined,
          heroImageHasTransparency: false,
          hasAnyCharts: false,
          similarJobs,
        } as JobData);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [pathname, baseData]);

  const jobData = useMemo(() => {
    const sourceData = dataFromHook ?? scriptData;
    if (!sourceData) return clientData;
    if (!clientData) return sourceData;

    const hookHasSimilarJobs =
      Array.isArray(sourceData.similarJobs) && sourceData.similarJobs.length > 0;
    const clientHasSimilarJobs =
      Array.isArray(clientData.similarJobs) && clientData.similarJobs.length > 0;
    const shouldUseClientSimilarJobs = !hookHasSimilarJobs && clientHasSimilarJobs;

    return {
      ...sourceData,
      similarJobs: shouldUseClientSimilarJobs ? clientData.similarJobs : sourceData.similarJobs,
    };
  }, [dataFromHook, scriptData, clientData]);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hookCount = Array.isArray(dataFromHook?.similarJobs) ? dataFromHook.similarJobs.length : -1;
    const scriptCount = Array.isArray(scriptData?.similarJobs) ? scriptData.similarJobs.length : -1;
    const clientCount = Array.isArray(clientData?.similarJobs) ? clientData.similarJobs.length : -1;
    const mergedCount = Array.isArray(jobData?.similarJobs) ? jobData.similarJobs.length : -1;
    const objectId = (jobData as { job?: { objectID?: string } } | null)?.job?.objectID ?? 'none';
    console.log('[SimilarJobsDebug][LayoutDefault] merge-state', {
      objectId,
      hookCount,
      scriptCount,
      clientCount,
      mergedCount,
    });
  }, [dataFromHook, scriptData, clientData, jobData]);
  const hasJobData = !!(jobData?.job);
  // Skeleton-first SRP -> job navigation (utils/view-transitions/job-preview-shell.ts): the shell
  // overlay comes off in the same commit the real page first renders, so it's never painted over
  // or after it. Layout effect, not effect, so the swap lands before that frame paints.
  useLayoutEffect(() => {
    if (!hasJobData) return;
    dismissJobPreviewShell();
    rememberJobPageHeaderBottom();
  }, [hasJobData]);
  // Real hydration target now (see below), not a throwaway static-match store — isClientSide must
  // reflect reality (false on the server, true on the client) so the client's post-hydration store
  // is the real, redux-persist-backed one, matching what job.client.ts's separate island used to
  // create for itself before this fix consolidated everything into one hydrated tree.
  const jobStore = useMemo(
    () => createStore({ ...pageContext, data: jobData ?? null } as any),
    [(jobData as { job?: { objectID?: string } } | null)?.job?.objectID]
  );

  // Indexable-page hydration rework: previously, when job data became available after a client
  // fetch (CSR nav to a new job), this effect explicitly re-invoked the island loader so the
  // *separate* #job-app tree would pick up the new data. Now that JobDetailPage renders directly
  // inside this same component's tree (see the single #job-ssr container below, hydrated in place
  // by job.client.ts rather than swapped), a new jobData value flows to <JobDetailPage> through
  // normal React re-rendering — no explicit remount call needed.

  // Real hydration (indexable-page hydration rework, following the SRP fix): render the exact
  // same provider stack job.client.ts's mountJobIsland used to build for its separate #job-app
  // island, here in #job-ssr instead — IntlProvider/AuthProvider/UserProvider/
  // FeedbackModalProvider/ToastProvider/JobIslandProvider were previously only added client-side,
  // which is why the old #job-app island had to be a *new* tree rather than a hydration of this
  // one. Matching them here is what makes hydrateRoot() on #job-ssr possible (see job.client.ts).
  const jobMessages = useMemo(() => loadLocaleData(jobLocale), [jobLocale]);
  const jobUrlOriginal =
    typeof window !== 'undefined' ? window.location.href : pathname;
  const jobIslandValue = useMemo(
    () => ({
      locale: jobLocale,
      pathname,
      urlOriginal: jobUrlOriginal,
      user: (pageContext?.user ?? null) as any,
    }),
    [jobLocale, pathname, jobUrlOriginal, pageContext?.user],
  );
  const jobHasBackofficeAccess = Boolean((pageContext as { hasBackofficeAccess?: boolean })?.hasBackofficeAccess);
  const jobPageContextUserState =
    (pageContext as { userState?: Record<string, unknown> })?.userState &&
    typeof (pageContext as { userState?: Record<string, unknown> }).userState === 'object'
      ? ((pageContext as { userState?: Record<string, unknown> }).userState as Record<string, unknown>)
      : {};

  return (
    <>
      <div id="job-wrapper" style={{ position: 'relative', minHeight: '100vh' }}>
        <div id="job-ssr">
            <Provider store={jobStore}>
              <IntlProvider
                messages={jobMessages}
                locale={jobLocale}
                defaultLocale="en"
                onError={onIntlError}
              >
                <FooterStringsProvider strings={footerStrings}>
                  <AuthProvider
                    initialUser={(pageContext?.user ?? null) as any}
                    initialHasBackofficeAccess={jobHasBackofficeAccess}
                  >
                    <UserProvider
                      initialUser={(pageContext?.user ?? null) as any}
                      initialHasBackofficeAccess={jobHasBackofficeAccess}
                      initialUserState={jobPageContextUserState as any}
                    >
                      <FeedbackModalProvider>
                        <ToastProvider>
                          <ToastContainer />
                          <SetupIntroDialog />
                          <PostSignupAlertNudge />
                          <JourneyBridge />
                          <FromAssistantStrip />
                          <JobIslandProvider value={jobIslandValue}>
                            <MinimalChrome>
                              {hasJobData && JobDetailPage ? (
                                <JobDetailPage initialData={jobData as any} initialLocale={jobLocale} />
                              ) : (
                                <div className="min-h-screen bg-jc-page" />
                              )}
                            </MinimalChrome>
                          </JobIslandProvider>
                        </ToastProvider>
                      </FeedbackModalProvider>
                    </UserProvider>
                  </AuthProvider>
                </FooterStringsProvider>
              </IntlProvider>
            </Provider>
        </div>
      </div>
      {/* Marker only: pages/+onRenderClient.ts checks for it to take the job island path. The job
          data and userState used to be serialized here too, a second copy of what Vike already
          ships in pageContext (data, userState via passToClient); job.client.ts reads them from
          pageContext now, and this payload stays empty. */}
      {hasJobData && <script id="job-initial" type="application/json" dangerouslySetInnerHTML={{ __html: '{}' }} />}
    </>
  );
}

const SUPPORTED_LOCALES = ['en', 'fi', 'sv'] as const;

/** Path segments (without locale) that use the minimal layout (nav + content, no SearchProvider/Redux). */
const MINIMAL_ROUTE_SEGMENTS = new Set([
  'resources', 'tyoelama', 'arbetsliv',
  'about', 'tietoa-jobcrawlsista', 'om-jobcrawls',
  'for-employers', 'tyonantajille', 'for-arbetsgivare',
  'locations', 'sijainnit', 'platser',
  'authors',
  // The salary/tax calculator: translated path segment per locale (see utils/salary/tax-iceberg-url.ts).
  'tax-iceberg', 'veroprosenttilaskuri', 'loneraknare',
]);

/** Sub-routes that need full layout (SearchWrapper) even when under resources. */
const FULL_LAYOUT_SUBROUTES = new Set<string>([]);

/**
 * Locations hub (`/locations` and locale/localised-slug equivalents): static+island pattern
 * (see `locations.client.ts`). This is currently the only page under the `locations` segment
 * (no sub-routes), so an exact match is deliberate rather than a prefix match.
 */
const LOCATIONS_HUB_PATH_RE = /^\/(?:en\/locations|fi\/sijainnit|sv\/platser|locations|sijainnit|platser)\/?$/;

function isLocationsHubPath(pathname: string): boolean {
  return LOCATIONS_HUB_PATH_RE.test(String(pathname || ''));
}

/**
 * Salary hub (`/salary` exactly, and locale/localised-slug equivalents): static+island pattern
 * (see `salary.client.ts`). Deliberately excludes `/salary/:role` (the per-role page, a
 * separate static+island target with its own chart-hover interactivity) via the trailing-slash
 * anchor — no `[^/]+` segment is allowed after the hub segment.
 */
const SALARY_HUB_PATH_RE = /^\/(?:en\/salary|fi\/palkka|sv\/lon|salary|palkka|lon)\/?$/;

function isSalaryHubPath(pathname: string): boolean {
  return SALARY_HUB_PATH_RE.test(String(pathname || ''));
}

/**
 * Salary role page (`/salary/:role` — exactly one segment after the hub, and locale/localised
 * equivalents): static+island pattern (see `salary-role.client.ts`).
 */
const SALARY_ROLE_PATH_RE = /^\/(?:en\/salary|fi\/palkka|sv\/lon|salary|palkka|lon)\/[^/]+\/?$/;

function isSalaryRolePath(pathname: string): boolean {
  return SALARY_ROLE_PATH_RE.test(String(pathname || ''));
}

function isMinimalRoute(path: string): boolean {
  const normalized = String(path).replace(/\/+$/, '') || '/';
  const segments = String(path).split('/').filter(Boolean);
  if (segments.some((s) => FULL_LAYOUT_SUBROUTES.has(s.toLowerCase()))) return false;
  const first = segments[0]?.toLowerCase();
  const second = segments[1]?.toLowerCase();
  if (first && MINIMAL_ROUTE_SEGMENTS.has(first)) return true;
  if (first && ['en', 'fi', 'sv'].includes(first) && second && MINIMAL_ROUTE_SEGMENTS.has(second)) return true;
  return false;
}

/** Get locale from pathname (first segment if en/fi/sv). Client-side fallback when pageContext.locale isn't ready during hydration. */
function getLocaleFromPathname(pathname: string): (typeof SUPPORTED_LOCALES)[number] | null {
  const first = pathname.split('/').filter(Boolean)[0]?.toLowerCase();
  return first && (SUPPORTED_LOCALES as readonly string[]).includes(first) ? (first as (typeof SUPPORTED_LOCALES)[number]) : null;
}

// Utility to determine if this is the search page (homepage for the locale)
function isSearchPageVike(pageContext: { urlLogical?: string; urlPathname?: string; urlParsed?: { pathname?: string } } | null | undefined) {
  // Prefer urlPathname when available (vike provides it), else urlLogical. On client during hydration,
  // pageContext may not be ready yet—fall back to window.location.pathname to avoid showing SRP navbar on resource pages.
  const path =
    (pageContext as any)?.urlPathname ??
    (pageContext as any)?.urlLogical ??
    (pageContext as any)?.urlParsed?.pathname ??
    (typeof window !== 'undefined' ? window.location.pathname : '/');
  return isSearchPagePathname(path);
}

/** Wrapper that calls useProfilePreferencesSettingsSync. Renders null. Used so we can catch Redux Provider missing via ErrorBoundary. */
function ProfilePreferencesSettingsSyncGuard({ userId }: { userId: string | null | undefined }) {
  useProfilePreferencesSettingsSync(userId ?? null);
  return null;
}

/** Resolves userId for sync: use Auth context when available (e.g. after client session fetch), else pageContext from SSR. Calls hooks unconditionally so order stays stable across routes. */
function useEffectiveUserId(): string | null {
  const auth = useAuthOptional();
  const pageContext = usePageContext();
  if (auth?.user?.id) return auth.user.id;
  return (pageContext as { user?: { id?: string } })?.user?.id ?? null;
}

function LayoutContent({ children }: { children: ReactNode }) {
  const pageContext = usePageContext();
  const effectiveUserId = useEffectiveUserId();
  const pathnameForLocale =
    (pageContext as any)?.urlPathname ??
    (pageContext as any)?.urlLogical ??
    (typeof window !== 'undefined' ? window.location.pathname : '/');
  const locale = resolvePageLocale({
    locale: pageContext?.locale as string | undefined,
    urlPathname: pathnameForLocale,
  });
  // Memoize messages and footer strings (SSR-computed; footer never uses react-intl)
  const messages: Messages = useMemo(() => loadLocaleData(locale), [locale]);
  const footerStrings = useMemo(() => buildFooterStaticStrings(messages), [messages]);
  
  // Navigation context (optional so hook order is stable when outside provider)
  const navigation = useNavigationOptional();
  // Stable (useCallback), unlike the context value object, which is new on every provider render.
  const completeNavigation = navigation?.completeNavigation;
  
  useGaUserProperties();
  
  // Track initial load to prevent scroll/navigation on SSR hydration
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const previousPathnameRef = useRef<string | undefined>(pageContext?.urlPathname);

  // Mark initial load as complete after hydration
  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsInitialLoad(false);
      previousPathnameRef.current = pageContext?.urlPathname;
    }, 50);
    return () => clearTimeout(timeout);
  }, []);

  // Complete navigation when page context changes (actual navigation completed). Scroll is not
  // handled here: +onPageTransitionEnd scrolls a new page to the top and puts Back/Forward back
  // where the visitor was. Scrolling here too undid that Back restore, since this effect ran
  // again after it (it returned before recording the pathname, so it kept seeing a change).
  useEffect(() => {
    // Skip on initial load (hydration is not a navigation)
    if (isInitialLoad) return;

    if (previousPathnameRef.current === pageContext?.urlPathname) return;
    previousPathnameRef.current = pageContext?.urlPathname;

    if (completeNavigation) {
      // Small delay to ensure page has rendered
      const timeout = setTimeout(completeNavigation, 100);
      return () => clearTimeout(timeout);
    }
  }, [pageContext?.urlPathname, completeNavigation, isInitialLoad]);
  
  // Compute isSearchPage on every render to ensure it updates with client-side navigation
  const isSearchPage = isSearchPageVike(pageContext as any);
  const isSrpIsland = useSrpIsland();
  
  // Get server state from page data if available
  // Memoize to prevent creating new object references on every render
  const serverState = useMemo(() => (pageContext?.data as any)?.serverState, [pageContext?.data]);
  const searchData = useMemo(() => {
    // Use only pageContext.data here. Do not merge `useData()`: during SRP→SRP client navigations,
    // `useData()` can still hold the previous route's payload while `pageContext.data` is briefly empty;
    // merging would push stale `initialFilters` into SearchProvider and overwrite URL-aligned state.
    const data = (pageContext?.data as any) || {};
    const routeSynced = isLayoutClientRouteSyncedWithWindow(pageContext);
    // Avoid false positives: during CSR, window may already show /jobs while urlPathname/data lag one frame.
    const shouldWarnMissingSearchData =
      typeof window !== 'undefined' &&
      isSearchPage &&
      routeSynced &&
      layoutPageDataLacksSrpPayload(data);
    if (shouldWarnMissingSearchData) {
      console.warn('⚠️ [LayoutContent] pageContext.data missing initialQuery/initialResults', {
        hasData: !!pageContext?.data,
        dataKeys: pageContext?.data && typeof pageContext.data === 'object' ? Object.keys(pageContext.data) : [],
        urlPathname: pageContext?.urlPathname,
      });
    }
    return data;
  }, [pageContext, pageContext?.data, isSearchPage]);

  // Use window.location.pathname as fallback on client during hydration so we don't incorrectly
  // get pathname='/' when pageContext isn't ready yet (which would make isSearchPage true and show SRP navbar).
  const pathname =
    (pageContext as any)?.urlPathname ??
    (pageContext as any)?.urlLogical ??
    (typeof window !== 'undefined' ? window.location.pathname : '/');

  // Memoize SearchProvider props to prevent re-renders when searchData object reference changes
  const searchProviderProps = useMemo(
    () => ({
      indexName: searchData.indexName || getIndexName(locale),
      initialQuery: searchData.initialQuery || '',
      initialFilters: searchData.initialFilters || {},
      initialPage: searchData.initialPage || 0,
      initialResults: searchData.initialResults ?? null,
      initialSort: searchData.initialSort || 'relevance',
    }),
    [
      searchData.indexName,
      searchData.initialQuery,
      searchData.initialPage,
      searchData.initialResults,
      searchData.initialFilters,
      searchData.initialSort,
      locale,
    ],
  );

  // Memoize SearchWrapper props (Step 3: use lite shell on SRP to defer filter bar / mobile nav)
  const searchWrapperProps = useMemo(
    () => ({
      isSearchPage,
      useLiteShell: isSearchPage,
      serverState,
      ssrIsMobile: (pageContext?.data as any)?.ssrIsMobile,
      ssrIsXl: (pageContext?.data as any)?.ssrIsXl,
      initialFacetsData: searchData.facetsData,
    }),
    [isSearchPage, serverState, (pageContext?.data as any)?.ssrIsMobile, (pageContext?.data as any)?.ssrIsXl, searchData.facetsData],
  );

  const srpViewModel = (searchData as any).srpViewModel ?? null;
  // Only use for static render when we have a full view model (not the _fetch bootstrap).
  // pageContext.data carries the wire form (see features/srp/srp-view-model-wire.ts); this
  // re-attaches its initialResults/initialFacetsData from the top-level data fields.
  const fullSrpViewModel: SrpViewModel | null = resolveSrpViewModel(searchData);

  if (typeof window !== 'undefined' && (window as any).__SRP_DEBUG_LAYOUT__) {
    console.log('[LayoutContent] branch', {
      isSearchPage,
      isSrpIsland,
      hasSrpViewModel: !!srpViewModel,
      hasFullSrpViewModel: !!fullSrpViewModel,
    });
  }

  // Job detail: static shell then client swap (like SRP). Never render SearchWrapper+JobDetailPage for job route (avoids hydration mismatch).
  // Match both path formats: with locale (/en/job/...) and without (urlLogical: /job/...).
  const isJobPage = isJobDetailPathname(String(pathname || ''));

  // An error render on a job path (the +data hook threw render(404/410): job gone, legacy URL,
  // ghost row) arrives here with `children` being the _error page, not a job. JobRouteContent
  // ignores children and, with no job data, rendered a bare empty placeholder, so a visitor on a
  // dead job link (stale Google result, AI-assistant citation) got a blank page with only the
  // nav and footer, in production too. Render the error page through the normal chrome instead.
  const isErrorRender = Boolean(
    (pageContext as { is404?: boolean; abortStatusCode?: number } | undefined)?.is404 ||
      (pageContext as { abortStatusCode?: number } | undefined)?.abortStatusCode,
  );
  if (isJobPage && isErrorRender) {
    return (
      <FooterStringsProvider strings={footerStrings}>
        <MinimalChrome noPadding>{children}</MinimalChrome>
      </FooterStringsProvider>
    );
  }

  // Job data for hydration is read synchronously from `#job-initial` inside JobRouteContent so the first
  // client paint matches SSR (no empty shell / null Suspense fallback for crawlers).
  if (isJobPage) {
    return <JobRouteContent footerStrings={footerStrings} />;
  }

  // Progressive SRP takeover (no hydration): server-only static HTML + empty container + snapshot.
  // Backoffice: the backoffice shell alone (its own compact bar, breadcrumb, page background) +
  // content: no site navbar, no SearchWrapper and no site footer, so workbench tools keep the
  // screen height for their own panels. The shell has to be applied here rather than
  // as a Vike +Layout under pages/backoffice: pages/+onRenderClient.ts renders
  // `<LayoutDefault><Page/></LayoutDefault>` itself and never applies nested config Layouts, so a
  // +Layout there rendered on the server and then vanished on hydration.
  if (isBackofficePathname(pathname)) {
    // Loaded before render: statically on the server, by pages/+onRenderClient.ts in the browser.
    const BackofficeShell = getBackofficeShell();
    return BackofficeShell ? <BackofficeShell>{children}</BackofficeShell> : <>{children}</>;
  }

  // JobCrawls for teams (the data-product home and workspace pages, spec 15): its own frame with
  // the product's navigation instead of the job-seeker navbar and footer. Loaded like the
  // backoffice shell (route-components.ts).
  if (isTeamsPathname(pathname)) {
    const TeamsShell = getTeamsShell();
    return TeamsShell ? <TeamsShell>{children}</TeamsShell> : <>{children}</>;
  }

  // Outpost (the recruiter and employer product): its own frame and navigation instead of the
  // job-seeker navbar and footer, loaded the same way as the Teams shell.
  if (isOutpostPathname(pathname)) {
    const OutpostShell = getOutpostShell();
    return OutpostShell ? <OutpostShell>{children}</OutpostShell> : <>{children}</>;
  }

  // Tools pages (applied-jobs, saved-jobs, viewed-jobs, activity Sankey route, etc.): navbar + content via MinimalChrome
  // so page content renders in the main area without being wrapped in SearchWrapper (which can leave content blank).
  // No site footer: tool pages are app screens, like Scout (owner 2026-11-02).
  if (isToolsPage(pathname)) {
    return (
      <FooterStringsProvider strings={footerStrings}>
        <MinimalChrome withFooter={false}>{children}</MinimalChrome>
      </FooterStringsProvider>
    );
  }

  // Real hydration (SRP islands rework Phase 2): server-renders the SAME interactive SrpApp tree
  // the client will hydrate against, into one container (#srp-ssr). No separate empty #srp-app to
  // swap into, and no #srp-initial bootstrap script — pageContext.data carries the full view model
  // to the client via the standard passToClient mechanism (pages/+config.ts), and
  // pages/index/srp.client.ts calls hydrateRoot() directly on #srp-ssr. This replaces the old
  // "static shell + separate createRoot tree + visibility swap" pattern, which invalidated the
  // LCP timestamp of already-painted content every time the swap ran (see SRP islands rework
  // Phase 2 plan for the full root-cause analysis).
  // Loaded before render: statically on the server, by pages/+onRenderClient.ts in the browser.
  const SrpApp = isSearchPage && fullSrpViewModel && !isSrpIsland ? getSrpApp() : null;
  if (SrpApp && fullSrpViewModel) {
    const srpInitialUser = (pageContext?.user ?? null) as SessionUser | null;
    const srpHasBackofficeAccess = Boolean((pageContext as { hasBackofficeAccess?: boolean })?.hasBackofficeAccess);
    const srpMatchProfileCookieFallback =
      (searchData as { matchProfileCookieFallback?: unknown })?.matchProfileCookieFallback ?? null;
    const srpIsClientSide = Boolean((pageContext as { isClientSide?: boolean })?.isClientSide);
    const srpPageContextUserState = (pageContext as { userState?: Record<string, unknown> })?.userState ?? {};
    const srpInitialExperiments = (pageContext as { experiments?: Record<string, string> })?.experiments ?? {};
    return (
      <div id="srp-wrapper" style={{ position: 'relative', minHeight: '100dvh' }}>
        <div id="srp-ssr">
          <SrpApp
            initialViewModel={fullSrpViewModel}
            initialUser={srpInitialUser}
            initialHasBackofficeAccess={srpHasBackofficeAccess}
            initialMatchProfileCookieFallback={srpMatchProfileCookieFallback as any}
            isClientSide={srpIsClientSide}
            pageContextUserState={srpPageContextUserState}
            initialExperiments={srpInitialExperiments}
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <ErrorBoundary fallback={null}>
        <ProfilePreferencesSettingsSyncGuard userId={effectiveUserId} />
      </ErrorBoundary>
      <IntlProvider 
        messages={messages} 
        locale={locale} 
        defaultLocale={locale}
        onError={onIntlError}
      >
        <FooterStringsProvider strings={footerStrings}>
        <LocaleMismatchPrompt />
        {isSearchPage ? (
        // Rare fallback only: fullSrpViewModel is missing (double SSR failure — search backend
        // down AND the view-model build in +data.ts's own catch path also threw). No static
        // shell / swap here anymore — just the same providers every other route gets.
        <SearchProvider
          indexName={searchProviderProps.indexName}
          initialQuery={searchProviderProps.initialQuery}
          initialFilters={searchProviderProps.initialFilters}
          initialPage={searchProviderProps.initialPage}
          initialResults={searchProviderProps.initialResults}
          initialSort={searchProviderProps.initialSort}
          initialViewMode={searchData.initialLayout === 'map' ? 'map' : 'list'}
          syncToUrl={true}
        >
          <SearchWrapper {...searchWrapperProps}>
            {children}
          </SearchWrapper>
        </SearchProvider>
      ) : isLocationsHubPath(pathname) ? (
        // Real hydration (indexable-page hydration rework, same fix as SRP/job/salary/
        // best-companies-dimension): render the exact provider stack locations.client.ts used to
        // build for its separate #locations-app island, here directly into #locations-ssr instead.
        // IntlProvider/FooterStringsProvider already come from the outer tree above, matching what
        // the client used to duplicate locally.
        (() => {
          const locStore = createStore(pageContext);
          const locHasBackofficeAccess = Boolean(
            (pageContext as { hasBackofficeAccess?: boolean })?.hasBackofficeAccess,
          );
          return (
            <div id="locations-wrapper" style={{ position: 'relative', minHeight: '100vh' }}>
              <div id="locations-ssr">
                <Provider store={locStore}>
                  <AuthProvider
                    initialUser={(pageContext?.user ?? null) as any}
                    initialHasBackofficeAccess={locHasBackofficeAccess}
                  >
                    <UserProvider
                      initialUser={(pageContext?.user ?? null) as any}
                      initialHasBackofficeAccess={locHasBackofficeAccess}
                    >
                      <FeedbackModalProvider>
                        <ToastProvider>
                          <ToastContainer />
                          <FromAssistantStrip />
                          <MinimalChrome noPadding>{children}</MinimalChrome>
                        </ToastProvider>
                      </FeedbackModalProvider>
                    </UserProvider>
                  </AuthProvider>
                </Provider>
              </div>
            </div>
          );
        })()
      ) : isMinimalRoute(pathname) ? (
        <MinimalChrome noPadding>{children}</MinimalChrome>
      ) : isSalaryHubPath(pathname) || isSalaryRolePath(pathname) ? (
        // Real hydration (indexable-page hydration rework, same fix as SRP/job): render the exact
        // provider stack salary.client.ts/salary-role.client.ts used to build for their separate
        // #salary-app/#salary-role-app islands, here directly — Provider(store)/AuthProvider/
        // FeedbackModalProvider/ToastProvider were previously only added client-side, which is why
        // the old island had to be a *new* tree rather than a hydration of this one. IntlProvider
        // and FooterStringsProvider are already provided by the outer tree above with the same
        // values the client used to duplicate locally, so they're not repeated here. Matching this
        // shape is what makes hydrateRoot() on #salary-ssr/#salary-role-ssr possible.
        (() => {
          const hubStore = createStore(pageContext);
          const hubHasBackofficeAccess = Boolean(
            (pageContext as { hasBackofficeAccess?: boolean })?.hasBackofficeAccess,
          );
          const hubContainerId = isSalaryHubPath(pathname) ? 'salary-ssr' : 'salary-role-ssr';
          const hubWrapperId = isSalaryHubPath(pathname) ? 'salary-wrapper' : 'salary-role-wrapper';
          return (
            <div id={hubWrapperId} style={{ position: 'relative', minHeight: '100vh' }}>
              <div id={hubContainerId}>
                <Provider store={hubStore}>
                  <AuthProvider
                    initialUser={(pageContext?.user ?? null) as any}
                    initialHasBackofficeAccess={hubHasBackofficeAccess}
                  >
                    <UserProvider
                      initialUser={(pageContext?.user ?? null) as any}
                      initialHasBackofficeAccess={hubHasBackofficeAccess}
                    >
                      <FeedbackModalProvider>
                        <ToastProvider>
                          <ToastContainer />
                          <FromAssistantStrip />
                          <SearchProvider
                            indexName={getIndexName(locale)}
                            initialQuery={''}
                            initialFilters={{}}
                            initialPage={0}
                            initialResults={null}
                            initialSort={'relevance'}
                            syncToUrl={false}
                            localeOverride={locale}
                          >
                            <SearchWrapper isSearchPage={false}>{children}</SearchWrapper>
                          </SearchProvider>
                        </ToastProvider>
                      </FeedbackModalProvider>
                    </UserProvider>
                  </AuthProvider>
                </Provider>
              </div>
            </div>
          );
        })()
      ) : (
        <SearchProvider
          indexName={getIndexName(locale)}
          initialQuery={''}
          initialFilters={{}}
          initialPage={0}
          initialResults={null}
          initialSort={'relevance'}
          syncToUrl={false}
        >
          <SearchWrapper
            isSearchPage={isSearchPage}
            serverState={serverState}
            initialFacetsData={searchData.facetsData}
          >
            {children}
          </SearchWrapper>
        </SearchProvider>
      )}
        </FooterStringsProvider>
      </IntlProvider>
    </>
  );
}

/** Wraps layout in IntlProvider so Page (and any child) always has intl during hydration. */
function LayoutDefaultWithIntl({ children }: { children: ReactNode }) {
  const pageContext = usePageContext();
  const pathnameForLocale =
    (pageContext as any)?.urlPathname ??
    (pageContext as any)?.urlLogical ??
    (typeof window !== 'undefined' ? window.location.pathname : '/');
  const locale = resolvePageLocale({
    locale: pageContext?.locale as string | undefined,
    urlPathname: pathnameForLocale,
  });
  const messages: Messages = useMemo(() => loadLocaleData(locale), [locale]);
  const initialUser = (pageContext?.user ?? null) as SessionUser | null;
  const initialHasBackofficeAccess = Boolean((pageContext as { hasBackofficeAccess?: boolean })?.hasBackofficeAccess);
  const initialUserState = (pageContext as { userState?: UserStateMap })?.userState;
  const initialExperiments = (pageContext as { experiments?: Record<string, string> })?.experiments ?? {};

  useLayoutEffect(() => {
    syncHtmlLangAttribute(locale);
  }, [locale]);

  return (
    <IntlProvider
      messages={messages}
      locale={locale}
      defaultLocale={locale}
      onError={onIntlError}
    >
      <AuthProvider initialUser={initialUser} initialHasBackofficeAccess={initialHasBackofficeAccess}>
        {/* TEC-508: every code-defined experiment's variant, resolved server-side per render
            path (see vike-handler.ts) — zero-flash for useExperiment() callers anywhere below. */}
        <ExperimentProvider initialVariants={initialExperiments}>
        {/* TEC-404: additive TanStack Query + Context layer, mounted alongside AuthProvider/Redux
            (not replacing them yet). Shares the client QueryClient singleton (query/queryClient.ts)
            with the SRP island (SrpApp.tsx) and job-detail island (job.client.ts) so a mutation from
            one surface is visible to the others — the same reasoning as createStore's
            clientStoreSingleton (see that file's history of the "each island got its own store"
            regression). Seeded from the same SSR pageContext AuthProvider/createStore already use. */}
        <UserProvider
          initialUser={initialUser}
          initialHasBackofficeAccess={initialHasBackofficeAccess}
          initialUserState={initialUserState}
        >
        <FeedbackModalProvider>
          <SalaryDisplayPreferenceProvider>
          <CompanyGroupingPreferenceProvider>
          <PayModePreferenceProvider>
          <SrpCardDisplayModeProvider>
          <SrpSalaryDotHoverProvider>
          <NavigationProvider>
            <ToastProvider>
              <ToastContainer />
              <SetupIntroDialog />
              <PostSignupAlertNudge />
              <JourneyBridge />
              <DemoAccountBanner />
              <PublicDemoBanner />
              <FromAssistantStrip />
              <ErrorBoundary fallback={<LayoutPageErrorFallback />}>
                <InspectorMount>
                  <LayoutContent>{children}</LayoutContent>
                </InspectorMount>
              </ErrorBoundary>
              <CookieConsentBanner />
              <FunctionalConsentPrompt />
              <HiredMomentHost />
            </ToastProvider>
          </NavigationProvider>
          </SrpSalaryDotHoverProvider>
          </SrpCardDisplayModeProvider>
          </PayModePreferenceProvider>
          </CompanyGroupingPreferenceProvider>
          </SalaryDisplayPreferenceProvider>
        </FeedbackModalProvider>
        </UserProvider>
        </ExperimentProvider>
      </AuthProvider>
    </IntlProvider>
  );
}

/**
 * Resource content routes that use ResourcePageLayout, which provides its own
 * navbar (ResourceNavbar). LayoutDefault must not wrap these in MinimalChrome
 * (which also renders a NavigationNavbar) — doing so would produce two headers.
 */
const RESOURCE_ROUTE_RE =
  /^\/(en\/resources|fi\/tyoelama|sv\/arbetsliv)(\/|$)|^\/(resources|tyoelama|arbetsliv)(\/|$)/;

const JOB_SEARCH_GUIDE_ROUTE_RE =
  /^\/(en\/job-search|fi\/tyonhaku|sv\/jobbsok)(\/|$)|^\/(job-search|tyonhaku|jobbsok)(\/|$)/;

/**
 * Hosted employer careers pages (TEC-488): white-label, deliberately chrome-free — the page
 * IS the employer's page, so no JobCrawls navbar/footer may wrap it (its own footer carries
 * the attribution). Same bypass as resource routes.
 */
const CAREERS_ROUTE_RE = /^\/(en\/careers|fi\/ura|sv\/karriar)(\/|$)|^\/(careers|ura|karriar)(\/|$)/;

/** Personal websites (TEC-809), also on a member's own domain (`/__site`, spec 28): chrome-free like the careers pages. */
const PERSONAL_SITE_ROUTE_RE = /^(\/(en|fi|sv))?\/p\/[^/]+\/?$|^\/__site$/;

function isResourceRoute(pathname: string): boolean {
  const p = String(pathname || '');
  return RESOURCE_ROUTE_RE.test(p) || JOB_SEARCH_GUIDE_ROUTE_RE.test(p);
}

export default function LayoutDefault({ children }: { children: ReactNode }) {
  // The display and text faces (Onest and Inter, latin subsets) for every app page. The root +Head preloads them, but
  // any page with its own +Head (employers, locations, roles, company...) replaces the root one, so
  // those pages fetched the fonts late and every heading swapped from the fallback after first paint.
  // React emits this preload during SSR for every page, the same way web-static's +Layout does.
  preload('/assets/fonts/onest/onest-latin.woff2', { as: 'font', type: 'font/woff2', crossOrigin: 'anonymous' });
  preload('/assets/fonts/inter/inter-latin.woff2', { as: 'font', type: 'font/woff2', crossOrigin: 'anonymous' });
  const pageContext = usePageContext();
  const pathname =
    (pageContext as any)?.urlPathname ??
    (pageContext as any)?.urlLogical ??
    (typeof window !== 'undefined' ? window.location.pathname : '/');

  if (isResourceRoute(pathname) || CAREERS_ROUTE_RE.test(String(pathname || '')) || PERSONAL_SITE_ROUTE_RE.test(String(pathname || ''))) {
    // These pages provide their own layout/chrome via their page-level Layout
    // config (ResourcePageLayout / white-label careers pages) — bypass all providers.
    // StaticInspectorMount is the one exception: it's a no-op wrapper unless a reviewer has
    // opted in server-side (see vike-handler.ts's canReviewTranslations), so it doesn't
    // reintroduce the provider tree this bypass exists to avoid.
    const i18nInspectorEnabled = Boolean((pageContext as { i18nInspectorEnabled?: boolean })?.i18nInspectorEnabled);
    return <StaticInspectorMount enabled={i18nInspectorEnabled}>{children}</StaticInspectorMount>;
  }

  return <LayoutDefaultWithIntl>{children}</LayoutDefaultWithIntl>;
}
